import { setPageMeta } from "@/lib/seo";
import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { services } from "@/data/services";
import { PageCTA } from "@/components/wve/PageCTA";

const extras = [
  {
    n: "06",
    title: "Live & Hybrid",
    tag: "Broadcast-quality live production across the UK — 12× Blackmagic cameras, full crew.",
    to: "/live",
  },
  {
    n: "07",
    title: "The Studios",
    tag: "Photo, video & podcast studios in Wickford — hire from £45.",
    to: "/studios",
  },
];

export default function ServicesPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
    setPageMeta(
      "Our Services — Video, Live Streams & Studios | WVE Studios",
      "Every service under the WVE Studios roof: video production, artist showreels, business content & promo, event production, dance show filming, live & hybrid broadcast and studio hire in Essex.",
      "/services"
    );
  }, []);

  const items = [
    ...services.map((s) => ({ n: s.n, title: s.title, tag: s.tag, to: `/services/${s.slug}` })),
    ...extras,
  ];

  return (
    <main data-testid="services-page">
      <section className="bg-[#0A0A0A] text-white pt-32 sm:pt-40 pb-20 relative overflow-hidden">
        <span className="absolute -right-24 top-0 font-display text-[20rem] leading-none text-white/[0.05] select-none hidden lg:block">ALL</span>
        <div className="max-w-[1600px] mx-auto px-6 lg:px-12 relative">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            <p className="text-sm uppercase tracking-[0.3em] font-semibold text-[#E80042] mb-4">Services</p>
            <h1 className="font-display uppercase leading-[0.95] text-5xl sm:text-7xl lg:text-8xl tracking-tight">
              Everything<br /><span className="text-[#E80042]">we do.</span>
            </h1>
            <p className="mt-8 text-lg sm:text-xl text-white/70 max-w-2xl leading-relaxed">
              From a single edit to a full hybrid broadcast — every service under the WVE Studios roof, all bespoke to your budget.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-16 sm:py-24" data-testid="services-list-section">
        <div className="max-w-[1600px] mx-auto px-6 lg:px-12 divide-y divide-black/10 border-y border-black/10">
          {items.map((s, i) => (
            <motion.div
              key={s.to}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.5, delay: (i % 4) * 0.05 }}
            >
              <Link
                to={s.to}
                data-testid={`services-list-item-${i + 1}`}
                className="group grid sm:grid-cols-12 gap-4 items-center py-8 sm:py-10 hover:bg-white transition-colors duration-300 px-2 sm:px-6"
              >
                <span className="sm:col-span-1 font-display text-2xl text-[#E80042]">{s.n}</span>
                <h2 className="sm:col-span-4 font-display uppercase text-3xl sm:text-4xl lg:text-5xl tracking-tight group-hover:text-[#E80042] group-hover:translate-x-2 transition-[color,transform] duration-300">
                  {s.title}
                </h2>
                <p className="sm:col-span-6 text-[#555555] text-base sm:text-lg leading-relaxed max-w-xl">{s.tag}</p>
                <span className="sm:col-span-1 justify-self-end w-11 h-11 rounded-full border-2 border-[#0A0A0A] flex items-center justify-center text-lg -rotate-45 group-hover:rotate-0 group-hover:bg-[#E80042] group-hover:border-[#E80042] group-hover:text-white transition-[transform,background-color,border-color,color] duration-300">
                  →
                </span>
              </Link>
            </motion.div>
          ))}
        </div>
      </section>

      <PageCTA context="New Project" title="Not sure what you need?" subtitle="Tell us the idea — we'll figure out the rest." />
    </main>
  );
}
